import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import EmailsTab from "./admin/EmailsTab";
import AuditTab from "./admin/AuditTab";
import { ExportDbButton } from "./admin/ExportDbButton";
import BugsTab from "./admin/BugsTab";
import CMLTab from "./admin/CMLTab";
import {
  CheckCircle2, AlertTriangle, Loader2, Search, ChevronDown, ChevronRight,
  Users, Activity, Beaker, Trash2, RotateCcw, ArrowUpDown, X, Mail, ListChecks, Bug, Network,
} from "lucide-react";

// Admin-only dashboard with 4 tabs: Lab health, Users, Visits, Emails.
export default function AdminLabsHealth() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("labs"); // labs | users | visits | emails
  const [err, setErr] = useState(null);

  // Lab health state (existing)
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  // Users state
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);

  // Visits state
  const [visits, setVisits] = useState(null);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Initial fetch: lab health (already on /admin/labs by default)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/labs/health");
        setReport(data);
      } catch (e) {
        if (e?.response?.status === 403) {
          setErr("Admin only — you don't have access to this page.");
        } else {
          setErr(e?.response?.data?.detail || e.message);
        }
      } finally {
        setReportLoading(false);
      }
    })();
  }, []);

  // Lazy-load Users tab data
  const loadUsers = () => {
    setUsersLoading(true);
    api.get("/admin/users/summary")
      .then((r) => setUsers(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setUsersLoading(false));
  };
  useEffect(() => {
    if (tab !== "users" || users) return;
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Lazy-load Visits tab data
  useEffect(() => {
    if (tab !== "visits" || visits) return;
    setVisitsLoading(true);
    api.get("/admin/visits/summary")
      .then((r) => setVisits(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setVisitsLoading(false));
  }, [tab, visits]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-100">
        <Navbar />
        <div className="max-w-md mx-auto py-32 text-center space-y-4">
          <AlertTriangle className="mx-auto text-[#F59E0B]" size={32} />
          <h1 className="font-display font-black text-2xl">Access denied</h1>
          <p className="text-zinc-400 text-sm font-mono">{err}</p>
          <button
            onClick={() => nav("/dashboard")}
            className="px-4 py-2 border border-zinc-700 hover:border-[#00E5FF] text-zinc-200 hover:text-[#00E5FF] text-xs font-mono uppercase tracking-widest transition-colors"
          >
            back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="admin-labs-health">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
              // admin console
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-1">Operations</h1>
            <p className="text-zinc-400 text-sm">
              Lab health, user growth, and lab usage — admin only.
            </p>
          </div>
          <ExportDbButton />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-900 mb-6 gap-1" data-testid="admin-tabs">
          <TabBtn id="labs" active={tab === "labs"} onClick={() => setTab("labs")} icon={<Beaker size={12} />}>
            Lab health
          </TabBtn>
          <TabBtn id="users" active={tab === "users"} onClick={() => setTab("users")} icon={<Users size={12} />}>
            Users
          </TabBtn>
          <TabBtn id="visits" active={tab === "visits"} onClick={() => setTab("visits")} icon={<Activity size={12} />}>
            Visits
          </TabBtn>
          <TabBtn id="emails" active={tab === "emails"} onClick={() => setTab("emails")} icon={<Mail size={12} />}>
            Emails
          </TabBtn>
          <TabBtn id="audit" active={tab === "audit"} onClick={() => setTab("audit")} icon={<ListChecks size={12} />}>
            Audit
          </TabBtn>
          <TabBtn id="bugs" active={tab === "bugs"} onClick={() => setTab("bugs")} icon={<Bug size={12} />}>
            Bugs
          </TabBtn>
          <TabBtn id="cml" active={tab === "cml"} onClick={() => setTab("cml")} icon={<Network size={12} />}>
            CML
          </TabBtn>
        </div>

        {tab === "labs" && (
          <LabHealthTab
            report={report}
            loading={reportLoading}
            filter={filter} setFilter={setFilter}
            search={search} setSearch={setSearch}
            openId={openId} setOpenId={setOpenId}
          />
        )}
        {tab === "users" && <UsersTab data={users} loading={usersLoading} reload={loadUsers} adminEmail={user?.email} />}
        {tab === "visits" && <VisitsTab data={visits} loading={visitsLoading} />}
        {tab === "emails" && <EmailsTab adminEmail={user?.email} />}
        {tab === "audit" && <AuditTab />}
        {tab === "bugs" && <BugsTab />}
        {tab === "cml" && <CMLTab adminEmail={user?.email} />}

        <div className="mt-8 text-[11px] text-zinc-600 font-mono">
          signed in as <span className="text-zinc-300">{user?.email}</span>
        </div>
      </main>
    </div>
  );
}

function TabBtn({ id, active, onClick, icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={`admin-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-widest font-mono border-b-2 transition-colors ${
        active
          ? "border-[#00E5FF] text-[#00E5FF]"
          : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {icon}{children}
    </button>
  );
}

// ============================================================
// Lab health tab (existing UI extracted as-is)
// ============================================================
function LabHealthTab({ report, loading, filter, setFilter, search, setSearch, openId, setOpenId }) {
  if (loading) {
    return <div className="py-16 text-center"><Loader2 className="text-[#00E5FF] animate-spin inline-block" size={28} /></div>;
  }
  if (!report) return null;
  const labs = (report.labs || []).filter((l) => {
    if (filter === "issues" && l.ok) return false;
    if (filter === "clean" && !l.ok) return false;
    if (search && !`${l.id} ${l.title} ${l.topic}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="admin-summary">
        <Tile label="total labs" value={report.total_labs} color="text-zinc-100" />
        <Tile label="with issues" value={report.labs_with_issues}
          color={report.labs_with_issues ? "text-[#EF4444]" : "text-[#00FF41]"} testid="admin-tile-issues" />
        <Tile label="total errors" value={report.total_issues}
          color={report.total_issues ? "text-[#EF4444]" : "text-[#00FF41]"} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search labs…"
            className="w-full bg-black border border-zinc-800 pl-8 pr-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
            data-testid="admin-search"
          />
        </div>
        {["all", "issues", "clean"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest font-mono border transition-colors ${
              filter === f
                ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
            }`}
            data-testid={`admin-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="divide-y divide-zinc-900 border border-zinc-900" data-testid="admin-lab-list">
        {labs.length === 0 && (
          <div className="px-4 py-6 text-center text-xs text-zinc-500 font-mono">no labs match</div>
        )}
        {labs.map((lab) => (
          <LabRow key={lab.id} lab={lab}
            open={openId === lab.id}
            onToggle={() => setOpenId(openId === lab.id ? null : lab.id)} />
        ))}
      </div>
    </>
  );
}

// ============================================================
// Users tab
// ============================================================
function UsersTab({ data, loading, reload, adminEmail }) {
  const [selected, setSelected] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, destructive }

  if (loading || !data) {
    return <div className="py-16 text-center"><Loader2 className="text-[#00E5FF] animate-spin inline-block" size={28} /></div>;
  }
  const { totals, new_signups, monthly_revenue_usd, pro_price_usd, recent_signups } = data;

  const adminLower = (adminEmail || "").toLowerCase();
  const eligible = recent_signups.filter((u) => (u.email || "").toLowerCase() !== adminLower);
  const allSelected = eligible.length > 0 && eligible.every((u) => selected.has(u.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(eligible.map((u) => u.id)));
  };

  const afterAction = () => { setSelected(new Set()); reload(); };

  const doDelete = (u) => setConfirm({
    title: "Delete user",
    message: `Delete ${u.email}? This cannot be undone.`,
    destructive: true,
    confirmLabel: "Delete user",
    onConfirm: async () => {
      setBusyId(u.id);
      try {
        await api.delete(`/admin/users/${u.id}`);
        toast.success(`Deleted ${u.email}`);
        afterAction();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally { setBusyId(null); setConfirm(null); }
    },
  });

  const doReset = (u) => setConfirm({
    title: "Reset user",
    message: `Reset ${u.email} to free tier with a fresh monthly lab quota? Account and lab progress are preserved.`,
    confirmLabel: "Reset user",
    onConfirm: async () => {
      setBusyId(u.id);
      try {
        await api.post(`/admin/users/${u.id}/reset`);
        toast.success(`Reset ${u.email}`);
        afterAction();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally { setBusyId(null); setConfirm(null); }
    },
  });

  const doToggleTier = (u) => {
    const target = u.tier === "pro" ? "free" : "pro";
    setConfirm({
      title: `${target === "pro" ? "Upgrade" : "Downgrade"} user`,
      message: `Move ${u.email} from ${u.tier} → ${target}?`,
      confirmLabel: target === "pro" ? "Upgrade to Pro" : "Downgrade to Free",
      onConfirm: async () => {
        setBusyId(u.id);
        try {
          const { data: r } = await api.post(`/admin/users/${u.id}/toggle-tier`);
          toast.success(`${u.email} → ${r.tier}`);
          afterAction();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally { setBusyId(null); setConfirm(null); }
      },
    });
  };

  const doSendPasswordReset = (u) => setConfirm({
    title: "Send password reset",
    message: `Send password reset email to ${u.email}?\n\nA fresh reset link will be emailed and the link expires in 60 minutes.`,
    confirmLabel: "Send reset email",
    onConfirm: async () => {
      setBusyId(u.id);
      try {
        const { data: r } = await api.post(`/admin/users/${u.id}/send-password-reset`);
        if (r.sent) toast.success(`Password reset email sent to ${u.email}`);
        else toast.warning(`Token issued for ${u.email} but email delivery failed`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally { setBusyId(null); setConfirm(null); }
    },
  });

  const doBulkDelete = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const emails = eligible.filter((u) => selected.has(u.id)).map((u) => u.email);
    setConfirm({
      title: `Delete ${ids.length} user${ids.length === 1 ? "" : "s"}`,
      message: `Delete the following ${ids.length} user${ids.length === 1 ? "" : "s"}? This cannot be undone.\n\n${emails.slice(0, 8).join("\n")}${emails.length > 8 ? `\n…and ${emails.length - 8} more` : ""}`,
      destructive: true,
      confirmLabel: `Delete ${ids.length} user${ids.length === 1 ? "" : "s"}`,
      onConfirm: async () => {
        try {
          const { data: r } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
          toast.success(`Deleted ${r.deleted} user${r.deleted === 1 ? "" : "s"}`);
          afterAction();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally { setConfirm(null); }
      },
    });
  };

  const doResetAllPro = () => {
    setConfirm({
      title: "Reset all Pro users",
      message: `Downgrade all ${totals.pro} Pro user${totals.pro === 1 ? "" : "s"} back to free, wipe their Stripe ids, and delete their payment_transactions rows?\n\nThis is meant for clearing out Stripe test-mode (4242…) purchases. The admin account is never affected. Lab progress is preserved.\n\nThis cannot be undone.`,
      destructive: true,
      confirmLabel: `Reset ${totals.pro} pro → free`,
      onConfirm: async () => {
        try {
          const { data: r } = await api.post("/admin/users/reset-all-pro");
          toast.success(`Downgraded ${r.downgraded} · deleted ${r.transactions_deleted} transactions`);
          afterAction();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally { setConfirm(null); }
      },
    });
  };

  return (
    <div data-testid="admin-users-tab" className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3">
        <Tile label="total users" value={totals.all_users} color="text-zinc-100" testid="users-total" />
        <Tile label="pro subscribers" value={totals.pro} color="text-[#00E5FF]" testid="users-pro" />
        <Tile label="verified" value={totals.verified} color="text-[#00FF41]" />
        <Tile label="free tier" value={totals.free} color="text-zinc-400" />
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <Tile label="new today" value={new_signups.today} color="text-zinc-100" testid="users-new-today" />
        <Tile label="this week" value={new_signups.this_week} color="text-zinc-100" />
        <Tile label="this month" value={new_signups.this_month} color="text-zinc-100" />
        <Tile label={`MRR · pro × $${pro_price_usd}`} value={`$${monthly_revenue_usd.toLocaleString()}`}
          color="text-[#00FF41]" testid="users-mrr" />
      </div>

      {totals.pro > 0 && (
        <div className="bg-[#141416] border border-[#F59E0B]/30 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[11px] font-mono text-zinc-400 leading-relaxed">
            <span className="text-[#F59E0B]">{totals.pro}</span> pro subscriber{totals.pro === 1 ? "" : "s"} → inflating MRR by{" "}
            <span className="text-[#F59E0B]">${monthly_revenue_usd.toLocaleString()}</span>.
            If these came from Stripe test cards (4242…), reset them all back to free.
          </div>
          <button
            onClick={doResetAllPro}
            data-testid="reset-all-pro-btn"
            className="flex items-center gap-2 px-3 py-2 bg-[#F59E0B]/15 border border-[#F59E0B]/60 text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black text-[10px] uppercase tracking-widest font-mono transition-colors"
          >
            <RotateCcw size={11} />
            reset all pro → free
          </button>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
            recent signups · last 25
          </div>
          {selected.size > 0 && (
            <button
              onClick={doBulkDelete}
              data-testid="bulk-delete-btn"
              className="flex items-center gap-2 px-3 py-1.5 bg-[#EF4444]/15 border border-[#EF4444]/60 text-[#EF4444] hover:bg-[#EF4444] hover:text-black text-[10px] uppercase tracking-widest font-mono transition-colors"
            >
              <Trash2 size={11} />
              delete selected · {selected.size}
            </button>
          )}
        </div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="admin-users-table">
            <thead className="bg-[#141416] text-zinc-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    data-testid="select-all-checkbox"
                    className="accent-[#00E5FF]"
                  />
                </th>
                <th className="text-left px-3 py-2">email</th>
                <th className="text-left px-3 py-2">signed up</th>
                <th className="text-left px-3 py-2">verified</th>
                <th className="text-left px-3 py-2">tier</th>
                <th className="text-right px-3 py-2">labs done</th>
                <th className="text-right px-3 py-2">actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {recent_signups.map((u) => {
                const isAdmin = (u.email || "").toLowerCase() === adminLower;
                const isBusy = busyId === u.id;
                return (
                  <tr key={u.id} className="hover:bg-zinc-900/40" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2">
                      {!isAdmin && (
                        <input
                          type="checkbox"
                          checked={selected.has(u.id)}
                          onChange={() => toggleOne(u.id)}
                          data-testid={`select-user-${u.id}`}
                          className="accent-[#00E5FF]"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-100 truncate max-w-[260px]">{u.email}</td>
                    <td className="px-3 py-2 text-zinc-500">
                      {u.created_at ? u.created_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {u.verified
                        ? <CheckCircle2 size={12} className="text-[#00FF41]" />
                        : <span className="text-zinc-600">no</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={u.tier === "pro" ? "text-[#00E5FF]" : "text-zinc-500"}>
                        {u.tier}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-300">{u.labs_completed}</td>
                    <td className="px-3 py-2">
                      {isAdmin ? (
                        <span className="text-[10px] text-zinc-600 italic">admin</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <RowAction
                            label={u.tier === "pro" ? "downgrade" : "upgrade"}
                            icon={<ArrowUpDown size={11} />}
                            onClick={() => doToggleTier(u)}
                            disabled={isBusy}
                            testid={`toggle-tier-${u.id}`}
                            color="hover:border-[#00E5FF] hover:text-[#00E5FF]"
                          />
                          <RowAction
                            label="reset"
                            icon={<RotateCcw size={11} />}
                            onClick={() => doReset(u)}
                            disabled={isBusy}
                            testid={`reset-${u.id}`}
                            color="hover:border-[#F59E0B] hover:text-[#F59E0B]"
                          />
                          <RowAction
                            label="send reset"
                            icon={<Mail size={11} />}
                            onClick={() => doSendPasswordReset(u)}
                            disabled={isBusy}
                            testid={`send-pw-reset-${u.id}`}
                            color="hover:border-[#00E5FF] hover:text-[#00E5FF]"
                          />
                          <RowAction
                            label="delete"
                            icon={<Trash2 size={11} />}
                            onClick={() => doDelete(u)}
                            disabled={isBusy}
                            testid={`delete-${u.id}`}
                            color="hover:border-[#EF4444] hover:text-[#EF4444]"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          {...confirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function RowAction({ label, icon, onClick, disabled, testid, color }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={label}
      className={`flex items-center gap-1 px-2 py-1 border border-zinc-800 text-zinc-400 text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${color}`}
    >
      {icon}{label}
    </button>
  );
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, destructive }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="confirm-modal"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-[#141416] border border-zinc-800 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            {destructive && <AlertTriangle size={16} className="text-[#EF4444]" />}
            <h3 className="font-display font-black text-lg">{title}</h3>
          </div>
          <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-200" data-testid="confirm-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-zinc-400 whitespace-pre-line font-mono leading-relaxed">{message}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            data-testid="confirm-cancel"
            className="px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 text-[10px] uppercase tracking-widest font-mono transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="confirm-ok"
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-mono transition-colors border ${
              destructive
                ? "bg-[#EF4444] text-black border-[#EF4444] hover:bg-[#dc2626]"
                : "bg-[#00E5FF] text-black border-[#00E5FF] hover:bg-[#00b8cc]"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Visits tab
// ============================================================
function VisitsTab({ data, loading }) {
  if (loading || !data) {
    return <div className="py-16 text-center"><Loader2 className="text-[#00E5FF] animate-spin inline-block" size={28} /></div>;
  }
  const { sessions_today, top_labs, completion_per_lab } = data;
  // Show labs with any starts on top, then alphabetical
  const sorted = [...completion_per_lab].sort((a, b) =>
    (b.starts - a.starts) || a.lab_id.localeCompare(b.lab_id)
  );
  return (
    <div data-testid="admin-visits-tab" className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-3">
        <Tile label="sessions today" value={sessions_today} color="text-zinc-100" testid="visits-today" />
        <Tile label="catalog size" value={completion_per_lab.length} color="text-zinc-100" />
        <Tile label="labs with activity"
          value={completion_per_lab.filter((l) => l.starts > 0).length} color="text-zinc-100" />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">
          top 5 labs · by attempts
        </div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="admin-top-labs">
            <thead className="bg-[#141416] text-zinc-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-2">lab</th>
                <th className="text-right px-3 py-2">starts</th>
                <th className="text-right px-3 py-2">attempts</th>
                <th className="text-right px-3 py-2">completed</th>
                <th className="text-right px-3 py-2">completion %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {top_labs.map((l) => (
                <tr key={l.lab_id} className="hover:bg-zinc-900/40">
                  <td className="px-3 py-2 text-zinc-100">{l.lab_id}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{l.starts}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{l.attempts}</td>
                  <td className="px-3 py-2 text-right text-[#00FF41]">{l.completed}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={l.completion_rate_pct >= 50 ? "text-[#00FF41]" : "text-[#F59E0B]"}>
                      {l.completion_rate_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">
          completion rate · per lab
        </div>
        <div className="border border-zinc-900 overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-xs font-mono" data-testid="admin-completion-per-lab">
            <thead className="bg-[#141416] text-zinc-500 text-[10px] uppercase tracking-widest sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">lab</th>
                <th className="text-left px-3 py-2">title</th>
                <th className="text-right px-3 py-2">starts</th>
                <th className="text-right px-3 py-2">completed</th>
                <th className="text-right px-3 py-2">rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {sorted.map((l) => (
                <tr key={l.lab_id} className="hover:bg-zinc-900/40">
                  <td className="px-3 py-2 text-zinc-100">{l.lab_id}</td>
                  <td className="px-3 py-2 text-zinc-400 truncate max-w-[280px]">{l.title}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{l.starts}</td>
                  <td className="px-3 py-2 text-right text-[#00FF41]">{l.completed}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={l.completion_rate_pct >= 50 ? "text-[#00FF41]" : l.completion_rate_pct > 0 ? "text-[#F59E0B]" : "text-zinc-600"}>
                      {l.starts === 0 ? "—" : `${l.completion_rate_pct}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Shared cells
// ============================================================
function Tile({ label, value, color, testid }) {
  return (
    <div className="bg-[#141416] border border-zinc-900 px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">{label}</div>
      <div className={`text-3xl font-display font-black mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function LabRow({ lab, open, onToggle }) {
  return (
    <div data-testid={`admin-lab-${lab.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/40 text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {lab.ok ? (
            <CheckCircle2 size={16} className="text-[#00FF41] shrink-0" />
          ) : (
            <AlertTriangle size={16} className="text-[#EF4444] shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-zinc-500">{lab.id}</span>
              <span className="text-sm font-semibold text-zinc-100 truncate">{lab.title}</span>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
              {lab.topic} · {lab.device_count} device{lab.device_count === 1 ? "" : "s"} · {lab.check_count} check{lab.check_count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {lab.issues.length > 0 && (
          <span className="px-2 py-0.5 bg-[#EF4444]/15 border border-[#EF4444]/50 text-[#EF4444] text-[10px] font-mono uppercase tracking-widest shrink-0">
            {lab.issues.length} issue{lab.issues.length === 1 ? "" : "s"}
          </span>
        )}
        {open ? (
          <ChevronDown size={14} className="text-zinc-500 shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-zinc-500 shrink-0" />
        )}
      </button>
      {open && lab.issues.length > 0 && (
        <ul className="bg-black px-12 py-3 space-y-1.5 text-xs font-mono">
          {lab.issues.map((issue, i) => (
            <li key={i} className="flex items-start gap-2 text-[#EF4444]">
              <span className="text-zinc-500 shrink-0">[{issue.rule}]</span>
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
